import { FormSteps } from "@smartsuite/react-ui/lib/components/forms/FormSteps/FormSteps";
import type {
  FormStep,
  FormStepsVariant,
} from "@smartsuite/react-ui/lib/components/forms/FormSteps/FormSteps.constants";
import { type CSSProperties, useLayoutEffect, useRef } from "react";
import { useIntl } from "react-intl";

import type { FormPage, ProgressBarStyle } from "../../types/form";
import { messages } from "./ProgressBar.text";

import "./ProgressBar.sass";

export interface ProgressBarProps {
  pages: FormPage[];
  currentPageIndex: number;
  furthestVisitedIndex: number;
  config: ProgressBarStyle;
  accentColor?: string;
  onPageNavigate?: (index: number) => void;
}

export const ProgressBar = ({
  pages,
  currentPageIndex,
  furthestVisitedIndex,
  config,
  accentColor,
  onPageNavigate,
}: ProgressBarProps): JSX.Element | null => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const items = container.querySelectorAll<HTMLElement>(".form-steps-item");
    items.forEach((item, idx) => {
      const isVisited = idx <= furthestVisitedIndex && idx !== currentPageIndex;
      item.classList.toggle("is-visited", isVisited);
    });
  });

  // Center the current step when the bar overflows. Not scrollIntoView: it would also scroll
  // the page vertically. The browser clamps scrollLeft to the valid range.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const activeItem = container?.querySelector<HTMLElement>(".form-steps-item.is-active");
    if (!container || !activeItem) return;
    const offset = activeItem.getBoundingClientRect().left - container.getBoundingClientRect().left;
    container.scrollLeft += offset - (container.clientWidth - activeItem.offsetWidth) / 2;
  }, [currentPageIndex]);

  if (!config?.enabled || pages.length < 2) {
    return null;
  }

  const steps: FormStep[] = pages.map((page) => ({ label: page.name }));
  const variant: FormStepsVariant = config.style === "numbers" ? "rect" : "circle";
  const isFinished = furthestVisitedIndex >= pages.length;

  const handleStepClick = (index: number): void => {
    if (!onPageNavigate) return;
    if (index >= currentPageIndex) return;
    onPageNavigate(index);
  };

  // Override --solution-primary-color so FormSteps active/completed states use the form's
  // accent color. The card already scopes this variable, but this makes it explicit and
  // future-proof if the bar is ever moved outside the card again.
  const accentStyle = accentColor
    ? ({ "--solution-primary-color": accentColor } as CSSProperties)
    : undefined;

  return (
    <div
      ref={containerRef}
      className="form-steps-bar"
      style={accentStyle}
      aria-label={intl.formatMessage(messages.ariaLabel)}
    >
      <FormSteps
        steps={steps}
        currentStep={currentPageIndex}
        variant={variant}
        showLabels={config.page_names === "show"}
        isFinished={isFinished}
        onStepClick={handleStepClick}
      />
    </div>
  );
};
